import { deduplicateIntervals, filterOutUndefinedIntervals } from './intervals'
import { DateTime } from 'luxon'

// A break with both of its times set
type TimedBreak = Break & {
  startAt: number
  endAt: number
}

const breakIsTimed = (br: Break): br is TimedBreak =>
  br.startAt != null && br.endAt != null

const getNormalizedBreaks = (
  timeCard: Pick<TimeCard, 'startAt' | 'endAt' | 'breaks'>,
): TimedBreak[] => {
  const { startAt, endAt } = timeCard
  if (startAt === null) return []
  if (endAt === null) return []

  const validBreaks = filterOutUndefinedIntervals(timeCard.breaks)
    .filter((b) => b.startAt <= endAt && b.endAt >= startAt)
    .map((b) => ({
      ...b,
      startAt: b.startAt > startAt ? b.startAt : startAt,
      endAt: b.endAt < endAt ? b.endAt : endAt,
    }))

  return deduplicateIntervals(validBreaks)
}

export const totalBreaksInSeconds = (
  timeCard: Pick<TimeCard, 'startAt' | 'endAt' | 'breaks'>,
): number => {
  const breaks = getNormalizedBreaks(timeCard)
  return breaks.reduce((sum, b) => b.endAt - b.startAt + sum, 0)
}

export const totalWorkedInSeconds = (
  timeCard: Pick<TimeCard, 'startAt' | 'endAt' | 'breaks'>,
): number => {
  if (timeCard.endAt === null) return 0
  if (timeCard.startAt === null) return 0

  return timeCard.endAt - timeCard.startAt - totalBreaksInSeconds(timeCard)
}

export const totalEntriesDuration = (
  entries: Pick<Entry, 'duration'>[],
): number => {
  return entries.reduce((sum, entry) => sum + entry.duration, 0)
}

/**
 * The single reason a Time Card may not be submitted yet, named so the UI can
 * explain it. Each one has a matching
 * `features.timeLogging.submissionBlockedReasons.*` message.
 *
 * Two of them cannot reach that message as the UI stands: a day without a Time
 * Card offers "Log Time" rather than a Time Card at all (`Day`), and a future
 * Time Card's footer is a placeholder rather than a Submit button (`Footer`).
 * They are still named here because they still block submission.
 */
export type TimeCardSubmissionBlockedReason =
  | 'noTimeCard'
  | 'onLeave'
  | 'inFuture'
  | 'missingStartTime'
  | 'missingEndTime'
  | 'endTimeBeforeStartTime'
  | 'breakMissingTime'
  | 'breakOutsideWorkDay'
  | 'noTimeWorked'
  | 'exceedsTimeOffCap'
  | 'underAllocated'
  | 'overAllocated'

type SubmissionCheck = {
  timeCard: Pick<TimeCard, 'startAt' | 'endAt' | 'breaks' | 'id' | 'date'>
  entries: Pick<Entry, 'duration'>[]
  totalTimeOffInSeconds: number
  onLeave: boolean
  scheduledWorkHours?: string // undefined == not scheduled to work
}

// NOTE: These validations should be kept in sync
//       with TimeCard#valid_for_submission? in the API
//
// Returns undefined when the Time Card may be submitted, otherwise the first
// blocked reason found. Checks run from the most fundamental to the most
// incidental, so the reason surfaced is the one worth acting on first.
export const getTimeCardSubmissionBlockedReason = ({
  timeCard,
  entries,
  totalTimeOffInSeconds,
  onLeave,
  scheduledWorkHours,
}: SubmissionCheck): TimeCardSubmissionBlockedReason | undefined => {
  if (timeCard.id === 0) return 'noTimeCard'
  if (onLeave) return 'onLeave'
  if (timeCard.date > DateTime.now()) return 'inFuture'

  // No need to continue if TC is missing start/end time
  const { startAt, endAt } = timeCard
  if (startAt == null) return 'missingStartTime'
  if (endAt == null) return 'missingEndTime'
  if (endAt <= startAt) return 'endTimeBeforeStartTime'

  // Breaks need start/end times, and must not fall outside of the configured
  // work day. The two are separate blocked reasons so the UI can say which of
  // them a break is failing.
  const timedBreaks = timeCard.breaks.filter(breakIsTimed)
  if (timedBreaks.length !== timeCard.breaks.length) return 'breakMissingTime'

  const breaksAreWithinWorkDay = timedBreaks.every(
    (br) => br.startAt >= startAt && br.endAt <= endAt,
  )
  if (!breaksAreWithinWorkDay) return 'breakOutsideWorkDay'

  // The day is known to end after it starts by now, and breaks are clamped to
  // it, so breaks covering the whole day is the only way to work no time
  const totalWorked = totalWorkedInSeconds(timeCard)
  if (totalWorked <= 0) return 'noTimeWorked'

  const exceedsTimeOffCap =
    scheduledWorkHours !== undefined &&
    totalTimeOffInSeconds > 0 &&
    totalWorked + totalTimeOffInSeconds >
      parseFloat(scheduledWorkHours) * 60 ** 2
  if (exceedsTimeOffCap) return 'exceedsTimeOffCap'

  const totalAllocated = totalEntriesDuration(entries)
  if (totalAllocated < totalWorked) return 'underAllocated'
  if (totalAllocated > totalWorked) return 'overAllocated'

  return undefined
}

export const timeCardValidForSubmission = (check: SubmissionCheck): boolean =>
  getTimeCardSubmissionBlockedReason(check) === undefined

export const getTitleKey = (
  timeCard: Pick<TimeCard, 'submitted' | 'fullyApproved'>,
) => {
  if (timeCard.fullyApproved) return 'common.approved'
  if (timeCard.submitted) return 'common.submitted'

  return 'common.open'
}
